// 製作的四格切換器：進度 / 後台 / 動畫師 / 導演，放在有專案的頁面上方列左側；最上面有一條顏色細色帶，一眼認出現在在哪（lib/workspace.ts）。
//   - 每一格是真的連結（<a href>）：Ctrl / Cmd / Shift 點、滑鼠中鍵會開新分頁；一般點是同一個分頁切換
//   - Alt+1～4 一樣切（輸入框打字時不觸發）
//   - guard：有沒存的調整時，先走各頁自己的離開提醒（編輯頁 guardLeave、調整舞台 guard）；沒給 = 直接切
//   - 沒有權限（enabled = false）→ 什麼都不畫（連細色帶也沒有），畫面跟以前完全一樣
import { useCallback, useEffect, useRef, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Clapperboard, LayoutGrid, Palette, Settings, type LucideIcon } from 'lucide-react';
import Icon from './Icon';
import type { OverflowItem } from './OverflowMenu';
import {
  WORKSPACE_HINT, WORKSPACE_LABEL, WORKSPACE_ORDER, hotkeyWorkspace, isTypingTarget, opensInNewTab,
  recallSong, rememberSong, workspaceHotkey, workspaceHref, type WorkspaceKey,
} from '../lib/workspace';
import './WorkspaceSwitcher.css';

export const WORKSPACE_ICON: Record<WorkspaceKey, LucideIcon> = {
  progress: LayoutGrid,
  backend: Settings,
  animator: Palette,
  director: Clapperboard,
};

interface Props {
  enabled: boolean;
  projectId: string;
  /** 現在這個頁面算哪一個畫面 */
  current: WorkspaceKey;
  /** 現在選的歌（歌跟著走）；沒選 = 用這個分頁上一次記的 */
  songId?: string | null;
  /** 切走之前先問（有沒存的調整）；給 go 讓它決定要不要放行 */
  guard?: (go: () => void) => void;
  /** 點的是現在這一格：例如進度格在看一首歌時 → 回總覽 */
  onCurrent?: () => void;
}

/** 切換器要的「去哪」：給手機的「⋯」選單用同一套 */
export function useWorkspaceGo({ enabled, projectId, current, songId, guard, onCurrent }: Omit<Props, never>) {
  const navigate = useNavigate();
  useEffect(() => { if (enabled) rememberSong(projectId, songId); }, [enabled, projectId, songId]);
  const hrefOf = useCallback((key: WorkspaceKey) => workspaceHref(key, projectId, key === 'progress' ? null : (songId ?? recallSong(projectId))), [projectId, songId]);
  const latest = useRef({ current, guard, onCurrent, navigate, hrefOf });
  latest.current = { current, guard, onCurrent, navigate, hrefOf };
  const go = useCallback((key: WorkspaceKey) => {
    const l = latest.current;
    if (key === l.current) { l.onCurrent?.(); return; }
    const run = () => l.navigate(l.hrefOf(key));
    if (l.guard) l.guard(run); else run();
  }, []);
  return { go, hrefOf };
}

export default function WorkspaceSwitcher(props: Props) {
  const { enabled, current } = props;
  const { go, hrefOf } = useWorkspaceGo(props);
  const goRef = useRef(go);
  goRef.current = go;

  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      const k = hotkeyWorkspace(e);
      if (!k || isTypingTarget(e.target as HTMLElement | null)) return;
      e.preventDefault();
      goRef.current(k);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);

  if (!enabled) return null;

  const onClick = (e: MouseEvent<HTMLAnchorElement>, key: WorkspaceKey) => {
    if (e.defaultPrevented || opensInNewTab(e)) return;  // 交給瀏覽器：開新分頁
    e.preventDefault();
    go(key);
  };

  return (
    <>
      <nav className="ws-switch ovf-hide-narrow" aria-label="切換畫面" data-testid="ws-switch">
        {WORKSPACE_ORDER.map(key => (
          <a
            key={key}
            href={hrefOf(key)}
            className={`ws-switch__cell ws--${key}${key === current ? ' is-on' : ''}`}
            aria-current={key === current ? 'page' : undefined}
            title={`${WORKSPACE_HINT[key]}（${workspaceHotkey(key)}）`}
            data-workspace={key}
            onClick={(e) => onClick(e, key)}
          >
            <Icon icon={WORKSPACE_ICON[key]} size="sm" />
            <span className="ws-switch__text">{WORKSPACE_LABEL[key]}</span>
          </a>
        ))}
      </nav>
      {typeof document !== 'undefined' && createPortal(
        <div className={`ws-ribbon ws--${current}`} data-testid="ws-ribbon" data-workspace={current} aria-hidden="true" />,
        document.body,
      )}
    </>
  );
}

/** 手機：切換器收進「⋯」選單時的四個項目（沒有權限 = 空的） */
export function workspaceMenuItems(enabled: boolean, current: WorkspaceKey, go: (key: WorkspaceKey) => void): OverflowItem[] {
  if (!enabled) return [];
  return WORKSPACE_ORDER.map((key, i) => ({
    key: `ws-${key}`,
    label: WORKSPACE_LABEL[key],
    icon: WORKSPACE_ICON[key],
    hint: WORKSPACE_HINT[key],
    active: key === current || undefined,
    separatorBefore: i === 0 || undefined,
    onSelect: () => go(key),
  }));
}
